import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { getRelaySwitchPath } from 'src/app/services/LUM/lum.helper';

const VARIABLE_OPTIONS = [
  { value: 'temperature', labelKey: 'lumAlarmVariableTemperature', source_component: 'temperature', source_action: 'read', unit: '°C' },
  { value: 'current', labelKey: 'lumAlarmVariableCurrent', source_component: 'powermeter', source_action: 'current', unit: 'A' },
  { value: 'voltage', labelKey: 'lumAlarmVariableVoltage', source_component: 'powermeter', source_action: 'voltage', unit: 'V' }
] as const;

/** Opción de variable que corresponde a la pareja source_component / source_action de una alarma. */
export function findLumAlarmVariable(cfg: { source_component?: string; source_action?: string } | null | undefined) {
  return VARIABLE_OPTIONS.find(option =>
    option.source_component === cfg?.source_component
    && (option.source_component !== 'powermeter' || option.source_action === cfg?.source_action),
  );
}

const THRESHOLD_TYPE_OPTIONS = [
  { value: 'MAX', labelKey: 'lumAlarmConditionGreater' },
  { value: 'MIN', labelKey: 'lumAlarmConditionLess' },
  { value: 'MIN_MAX', labelKey: 'lumAlarmThresholdMinMax' },
] as const;

const ACTION_COMPONENTS = [
  { value: 'relay', labelKey: 'lumAlarmComponentRelayNotification' },
  { value: 'Notification', labelKey: 'lumAlarmComponentNotification' },
] as const;

const ACTION_COMMANDS = [
  { value: 'on', labelKey: 'lumAlarmActionOn' },
  { value: 'off', labelKey: 'lumAlarmActionOff' },
] as const;

const TIME_UNITS = [
  { value: 'seconds', labelKey: 'lumAlarmUnitSeconds' },
  { value: 'minutes', labelKey: 'lumAlarmUnitMinutes' },
] as const;

const CURRENT_MAX_VALUE =63;

export interface LumAlarmLimit { min: number; max: number; step: number; }

/**
 * Límites por variable (trigger_delay en segundos, como se muestra en el formulario).
 * Una clave de tipo de umbral (MAX / MIN / MIN_MAX) sobrescribe los límites base.
 */
export const LUM_ALARM_LIMITS: Record<string, any> = {
  current: {
    threshold: { min: 0, max: CURRENT_MAX_VALUE, step: 0.1 },
    hysteresis: { min: 0.1, max: 20, step: 0.1 },
    trigger_delay: { min: 0, max: 60, step: 1 },
    MIN: {
      threshold: { min: 1, max: CURRENT_MAX_VALUE, step: 0.1 },
      trigger_delay: { min: 0, max: 300, step: 1 },
    },
  },
  voltage: {
    threshold: { min: 85, max: 265, step: 1 },
    hysteresis: { min: 1, max: 50, step: 1 },
    trigger_delay: { min: 0, max: 60, step: 1 },
  },
  temperature: {
    threshold: { min: 40, max: 80, step: 1 },
    hysteresis: { min: 1, max: 10, step: 1 },
    trigger_delay: { min: 1, max: 60, step: 1 },
  },
};

/** Valores por defecto según variable y tipo de umbral (trigger_delay en ms). */
export const LUM_ALARM_DEFAULTS: Record<string, Record<string, any>> = {
  current: {
    MAX: { threshold: 32, hysteresis: 1, trigger_delay: 5000 },
    MIN: { threshold: 10, hysteresis: 1, trigger_delay: 300000 },
    MIN_MAX: { threshold_min: 10, threshold_max: 32, hysteresis_min: 1, hysteresis_max: 1, trigger_delay: 5000 },
  },
  voltage: {
    MAX: { threshold: 253, hysteresis: 5, trigger_delay: 5000 },
    MIN: { threshold: 207, hysteresis: 5, trigger_delay: 5000 },
    MIN_MAX: { threshold_min: 207, threshold_max: 253, hysteresis_min: 5, hysteresis_max: 5, trigger_delay: 5000 },
  },
  temperature: {
    MAX: { threshold: 55, hysteresis: 3, trigger_delay: 10000 },
  },
};

/** Borrador vacío para una alarma nueva. */
export function createEmptyLumAlarmDraft(): any {
  return {
    name: '',
    enabled: true,
    source_component: 'temperature',
    source_action: 'read',
    source_src_id: 0,

    threshold_type: 'MAX',

    // MIN_MAX
    threshold_min: 40,
    threshold_max: 80,
    hysteresis_min: 0,
    hysteresis_max: 0,

    // MIN / MAX
    threshold: 55,
    hysteresis: 3,

    trigger_actions: [{ component: 'relay', action: 'off', src_id :0 }],
    auto_clean: true,
    auto_clean_interval_ms: 60000,
    trigger_delay: 10000,
    read_interval: 10000,
  };
}



@Component({
  selector: 'app-modal-lum-alarm',
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './modal-lum-alarm.component.html',
  styleUrl: './modal-lum-alarm.component.scss',
})
export class ModalLumAlarmComponent implements OnInit {

  /** Alarma completa { id, cfg } al editar; ignorado al crear. */
  @Input() alarm: any;
  @Input() id: number | string | null = null;

  cfg: any;
  variable = '';
  /** Tipo de acción elegido; 'Notification' se guarda como trigger_actions vacío. */
  actionComponent = 'relay';

  readonly variableOptions = VARIABLE_OPTIONS;
  readonly thresholdTypeOptions = THRESHOLD_TYPE_OPTIONS;
  readonly actionComponents = ACTION_COMPONENTS;
  readonly actionCommands = ACTION_COMMANDS;
  readonly timeUnits = TIME_UNITS;
  readonly getRelaySwitchPath = getRelaySwitchPath;

  constructor(public activeModal: NgbActiveModal) {}

  ngOnInit(): void {
    if (this.id == null) {
      this.cfg = createEmptyLumAlarmDraft();
    } else {
      this.cfg = structuredClone(this.alarm?.cfg);
    }
    this.variable = findLumAlarmVariable(this.cfg)?.value ?? '';
    this.actionComponent = this.cfg?.trigger_actions?.length ? this.cfg.trigger_actions[0].component : 'Notification';
  }

  onVariableChange(value: string): void {
    const option = this.variableOptions.find(item => item.value === value);
    if (!option) {
      return;
    }
    this.cfg.source_component = option.source_component;
    this.cfg.source_action = option.source_action;

    this.applyDefaults();
  }

  onThresholdTypeChange(): void {
    this.applyDefaults();
  }

  /** Solo en alarmas nuevas: al editar se respetan los valores guardados. */
  private applyDefaults(): void {
    if (this.id == null) {
      Object.assign(this.cfg, LUM_ALARM_DEFAULTS[this.variable]?.[this.cfg.threshold_type]);
    }
  }

  onActionComponentChange(value: string): void {
    this.cfg.trigger_actions = value === 'Notification'
      ? []
      : [{ component: value, action: 'off', src_id: 0 }];
  }

  msToSeconds(ms: number | null | undefined): number {
    return Math.round((Number(ms) || 0) / 1000);
  }

  secondsToMs(seconds: number | null | undefined): number {
    return Math.round(Number(seconds) || 0) * 1000;
  }

  /** Excluye campos de solo lectura y los que no aplican según el tipo de umbral. */
  private buildAlarmPayload(): any {
    const { timestamp, ...cfg } = structuredClone(this.cfg);

    if (cfg.threshold_type === 'MIN_MAX') {
      const { threshold, hysteresis, ...payload } = cfg;
      return payload;
    }

    const { threshold_min, threshold_max, hysteresis_min, hysteresis_max, ...payload } = cfg;
    return payload;
  }

  cancel(): void {
    this.activeModal.dismiss();
  }

  save(): void {
    const payload = this.buildAlarmPayload();
    if (this.id != null) {
      this.activeModal.close({ id: this.id, cfg: payload });
    } else {
      this.activeModal.close(payload);
    }
  }

  getLabelForValue(value: string): string {
    return this.variableOptions.find(option => option.value === value)?.labelKey ?? '';
  }

  getUnitForValue(value: string): string {
    return this.variableOptions.find(option => option.value === value)?.unit ?? '';
  }

  /** Límite del campo para la variable y tipo de umbral actuales. */
  getLimit(field: 'threshold' | 'hysteresis' | 'trigger_delay'): LumAlarmLimit | undefined {
    const limits = LUM_ALARM_LIMITS[this.variable];
    return limits?.[this.cfg?.threshold_type]?.[field] ?? limits?.[field];
  }

  isOutOfRange(value: unknown, field: 'threshold' | 'hysteresis' | 'trigger_delay'): boolean {
    const limit = this.getLimit(field);
    const number = Number(value);
    return !!limit && (value === null || value === '' || !Number.isFinite(number)
      || number < limit.min || number > limit.max || !this.matchesStep(number, limit.step));
  }

  /** Tolerancia para evitar falsos errores de coma flotante (p. ej. 0.3 / 0.1). */
  private matchesStep(value: number, step: number): boolean {
    const steps = value / step;
    return Math.abs(steps - Math.round(steps)) < 1e-6;
  }

  /** Comprueba los campos que aplican según el tipo de umbral. */
  get hasInvalidValues(): boolean {
    const thresholds = this.cfg.threshold_type === 'MIN_MAX'
      ? [this.cfg.threshold_min, this.cfg.threshold_max]
      : [this.cfg.threshold];
    const hysteresis = this.cfg.threshold_type === 'MIN_MAX'
      ? [this.cfg.hysteresis_min, this.cfg.hysteresis_max]
      : [this.cfg.hysteresis];

    return thresholds.some(value => this.isOutOfRange(value, 'threshold'))
      || hysteresis.some(value => this.isOutOfRange(value, 'hysteresis'))
      || this.isOutOfRange(this.msToSeconds(this.cfg.trigger_delay), 'trigger_delay')
      || (this.cfg.threshold_type === 'MIN_MAX' && Number(this.cfg.threshold_min) >= Number(this.cfg.threshold_max));
  }

}
